// hooks/useInvestorInfoMBAPL.tsx
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import moment from "moment";
import { Eye, Edit } from "lucide-react";
import { AxiosError } from "axios";
import {
  useDeleteInvestorInfoMutation,
  useGetInvestorInfoMBAPL,
} from "@/services";
import { INVESTOR_TYPES, INVESTOR_SUB_TYPES_BY_TYPE,InvestorType } from "./AddEdit/consts";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { MoreVertical, Trash2 } from "lucide-react";

const useInvestorInfoMBAPL = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [openDialog, setOpenDialog] = useState(false);
  const [deletedRow, setDeletedRow] = useState<any>(null);

  /** FILTER STATES */
  const [investorType, setInvestorType] = useState<InvestorType | "">("");
  const [investorSubType, setInvestorSubType] = useState<string>("");

  /** Build API params */
  const params: any = {};
  if (investorType) params.investorType = investorType;
  if (investorSubType) params.investorSubType = investorSubType;

  /** Search button triggers API */
  const {
    data: investorInfoList,
    isLoading: isInvestorInfoLoading,
    refetch,
  } = useGetInvestorInfoMBAPL(params);

  const handleSearch = () => refetch();

  /** Delete Mutation */
  const { mutate: deleteInfo, isPending: isDeletePending } = useDeleteInvestorInfoMutation({
    onSuccess: () => {
      toast.success("Investor Information Deleted Successfully");
      queryClient.invalidateQueries({ queryKey: ["investor-info-mba-pl"] });
      setOpenDialog(false);
    },
    onError: (error: AxiosError | any) => {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.issues?.error ||
        "Something went wrong";

      toast.error(message);
    }
  });

  const onAdd = () => navigate("add");

  const onDeleteConfirm = () => {
    if (deletedRow?._id) deleteInfo({ id: deletedRow._id });
  };

  /** TABLE COLUMNS */
  const columns = [
    { 
      header: "Investor Type",  
      cell: (row: any) => (
        <b className="text-primary font-[600]">
          {String(row.row?.original?.investorType || "")}
        </b>
      ),
    },
    { header: "Sub Type", accessorFn: (r: any) => r.investorSubType },
    { header: "Type", accessorFn: (r: any) => r.type },
    { header: "Title", accessorFn: (r: any) => r.title },
    {
      header: "Date",
      accessorFn: (r: any) =>
        r.date ? moment(r.date).format("DD-MM-YYYY") : "-",
    },
    {
      header: "Year",
      accessorFn: (r: any) => r.year || "-",
    },
    {
      header: "Created On",
      accessorFn: (r: any) =>
        r.createdAt ? moment(r.createdAt).format("DD-MM-YYYY") : "-",
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreVertical className="w-5 h-5 cursor-pointer" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white border-0 shadow-md"
          >
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigate(`${row.row.original._id}/view`);
              }}
            >
              <Eye className="w-4 h-4" />
              View
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigate(`${row.row.original._id}/edit`);
              }}
            >
              <Edit className="w-4 h-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setDeletedRow(row.row.original);
                setOpenDialog(true);
              }}
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  /** SELECTED INVESTOR TYPE */
  const selectedInvestor = INVESTOR_TYPES.find(
    (i) => i.value === investorType
  );

  return {
    // filter states
    investorType,
    setInvestorType,
    investorSubType,
    setInvestorSubType,

    selectedInvestor,
    INVESTOR_SUB_TYPES_BY_TYPE,

    // table
    investorInfoList: investorInfoList || { data: { items: [] } },
    isInvestorInfoLoading,
    columns,

    // buttons
    handleSearch,
    onAdd,

    // delete dialog
    openDialog,
    setOpenDialog,
    deletedRow,
    isDeletePending,
    onDeleteConfirm,
  };
};

export default useInvestorInfoMBAPL;